import React, { useState } from 'react';
import { StorageService } from '../services/storageService.ts';
import './DataManager.css';

interface DataManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

const DataManager: React.FC<DataManagerProps> = ({ isOpen, onClose }) => {
  const [exportData, setExportData] = useState<string>('');
  const [importData, setImportData] = useState<string>('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  if (!isOpen) return null;

  const handleExport = () => {
    try {
      const data = StorageService.exportData();
      const jsonString = JSON.stringify(data, null, 2);
      setExportData(jsonString);
      setMessage({ type: 'success', text: '数据导出成功！' });
    } catch (error) {
      setMessage({ type: 'error', text: '数据导出失败：' + (error as Error).message });
    }
  };

  const handleImport = () => {
    try {
      const data = JSON.parse(importData);
      const success = StorageService.importData(data);
      if (success) {
        setMessage({ type: 'success', text: '数据导入成功！请刷新页面以应用更改。' });
        setImportData('');
      } else {
        setMessage({ type: 'error', text: '数据导入失败！' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: '数据格式错误：' + (error as Error).message });
    }
  };

  const handleClearAll = () => {
    if (window.confirm('确定要清除所有数据吗？此操作不可撤销！')) {
      StorageService.clearAll();
      setMessage({ type: 'success', text: '所有数据已清除！请刷新页面。' });
    }
  };

  const handleDownload = () => {
    if (!exportData) return;
    
    const blob = new Blob([exportData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-backup-${new Date().toISOString().split('T')[0]}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setImportData(content);
    };
    reader.readAsText(file);
  };

  const storageUsage = StorageService.getStorageUsage();

  return (
    <div className="data-manager-overlay">
      <div className="data-manager-modal">
        <div className="data-manager-header">
          <h2>数据管理</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        {message && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="data-manager-content">
          {/* 存储使用情况 */}
          <div className="storage-usage">
            <h3>存储使用情况</h3>
            <div className="usage-bar">
              <div 
                className="usage-fill" 
                style={{ width: `${Math.min(storageUsage.percentage, 100)}%` }}
              ></div>
            </div>
            <p>
              已使用: {(storageUsage.used / 1024 / 1024).toFixed(2)}MB / 
              {(storageUsage.total / 1024 / 1024).toFixed(2)}MB 
              ({storageUsage.percentage.toFixed(1)}%)
            </p>
          </div>

          {/* 数据导出 */}
          <div className="export-section">
            <h3>数据导出</h3>
            <p>导出所有聊天记录、配置和设置到本地文件</p>
            <div className="button-group">
              <button onClick={handleExport} className="primary-button">
                生成导出数据
              </button>
              {exportData && (
                <button onClick={handleDownload} className="secondary-button">
                  下载文件
                </button>
              )}
            </div>
            {exportData && (
              <div className="export-preview">
                <h4>导出数据预览：</h4>
                <textarea 
                  value={exportData} 
                  readOnly 
                  rows={10}
                  placeholder="导出数据将显示在这里..."
                />
              </div>
            )}
          </div>

          {/* 数据导入 */}
          <div className="import-section">
            <h3>数据导入</h3>
            <p>从文件导入数据（将覆盖现有数据）</p>
            <div className="import-options">
              <div className="file-upload">
                <input
                  type="file"
                  accept=".json"
                  onChange={handleFileUpload}
                  id="file-upload"
                />
                <label htmlFor="file-upload" className="file-upload-label">
                  选择文件
                </label>
              </div>
              <div className="manual-input">
                <textarea
                  value={importData}
                  onChange={(e) => setImportData(e.target.value)}
                  placeholder="或直接粘贴JSON数据..."
                  rows={8}
                />
              </div>
            </div>
            <div className="button-group">
              <button 
                onClick={handleImport} 
                disabled={!importData.trim()}
                className="primary-button"
              >
                导入数据
              </button>
            </div>
          </div>

          {/* 数据清理 */}
          <div className="clear-section">
            <h3>数据清理</h3>
            <p>清除所有本地存储的数据</p>
            <button onClick={handleClearAll} className="danger-button">
              清除所有数据
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataManager; 